import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Divider,
  IconButton
} from '@mui/material';
import { Close as CloseIcon, Gavel as GavelIcon } from '@mui/icons-material';

const TermsModal = ({ open, onClose, companyName = 'YinoWISP' }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 4,
          bgcolor: '#ffffff',
          color: '#0f172a',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2.5, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                border: '1.5px solid #bae6fd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <GavelIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="800" color="#0f172a">
                Términos y Condiciones del Servicio
              </Typography>
              <Typography variant="caption" color="#64748b" fontWeight="500">
                {companyName} • Vía Pública y Portal del Cliente
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#64748b', '&:hover': { bgcolor: '#f1f5f9' } }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ borderColor: '#e2e8f0', p: 3.5, color: '#334155', lineHeight: 1.7, bgcolor: '#ffffff' }}>
        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            1. Aspectos Generales
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            El presente contrato regula los términos y condiciones aplicables a los servicios de telecomunicaciones, acceso a internet y servicios digitales prestados por <strong>{companyName}</strong> a sus clientes en el territorio nacional. Al contratar o utilizar nuestros servicios, el usuario declara haber leído, comprendido y aceptado la totalidad de las cláusulas contenidas en este documento.
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            2. Prestación y Calidad del Servicio
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            {companyName} se compromete a brindar el servicio de internet de acuerdo al plan contratado por el cliente, garantizando la velocidad mínima según las normativas vigentes del organismo regulador (OSIPTEL). La disponibilidad del servicio puede verse temporalmente afectada por trabajos de mantenimiento programados o causas de fuerza mayor.
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            3. Facturación, Pagos y Pasarelas Autorizadas
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            Las facturas y recibos de cobro son emitidos mensualmente con una fecha de vencimiento establecida. En caso de mora o falta de pago dentro de la fecha límite, el servicio podrá ser suspendido automáticamente. El servicio será reactivado inmediatamente tras la confirmación de pago a través de nuestros medios autorizados en línea y presenciales.
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            El procesamiento de transacciones electrónicas mediante tarjetas de crédito, débito y transferencias es provisto de manera segura a través de la pasarela de pagos <strong>Openpay (Banco BBVA Perú)</strong> y pasarelas certificadas aliadas (Culqi, Yape, Plin), las cuales operan bajo rigurosos protocolos de cifrado y estándares internacionales de seguridad PCI-DSS.
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            4. Protección de Datos Personales (Ley N° 29733)
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            En cumplimiento de la Ley de Protección de Datos Personales de la República del Perú (Ley N° 29733), {companyName} garantiza la confidencialidad y tratamiento seguro de la información proporcionada por los usuarios. Los datos personales recabados serán utilizados exclusivamente para la gestión contractual, facturación, atención de soporte técnico y notificaciones del servicio, sin ser comercializados con terceros.
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            5. Política de Cambios y Devoluciones
          </Typography>
          <Typography variant="body2" color="#334155" paragraph>
            Dado que los servicios prestados corresponden a telecomunicaciones intangibles consumidas mensualmente, no aplican devoluciones físicas de producto. En caso de cobros duplicados o errores de facturación atribuibles a la empresa, se procederá con el reembolso correspondiente o la acreditación del saldo a favor del cliente en su siguiente ciclo de facturación tras la verificación respectiva.
          </Typography>
        </Box>

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" fontWeight="700" color="#0284c7" gutterBottom>
            6. Atención de Reclamos
          </Typography>
          <Typography variant="body2" color="#334155">
            Los clientes cuentan con acceso directo al Libro de Reclamaciones Virtual integrado en nuestra plataforma, conforme al Código de Protección y Defensa del Consumidor (Ley N° 29571) e INDECOPI.
          </Typography>
        </Box>

        <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2.5, border: '1px solid #e2e8f0' }}>
          <Typography variant="subtitle2" fontWeight="700" color="#0f172a" gutterBottom>
            7. Canales de Contacto Oficial del Comercio
          </Typography>
          <Typography variant="caption" color="#475569" display="block">
            • <strong>Teléfono de Atención / WhatsApp:</strong> +51 924 816 719<br />
            • <strong>Correo Electrónico de Soporte:</strong> soporte@yinowisp.com / yinonetwisp@gmail.com<br />
            • <strong>Horario de Atención:</strong> Lunes a Sábado de 8:00 am a 8:00 pm
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
        <Button
          onClick={onClose}
          variant="contained"
          sx={{
            bgcolor: '#0284c7',
            color: '#ffffff',
            fontWeight: 'bold',
            borderRadius: '8px',
            px: 3.5,
            py: 0.8,
            boxShadow: 'none',
            '&:hover': { bgcolor: '#0369a1' }
          }}
        >
          Aceptar y Entendido
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsModal;
